import { useState, useEffect, useCallback } from 'react';
import type { CustomFormData } from '../types/form.types';
import { useAuth } from '@contexts/AuthContext';
import { apiService } from '@services/api';
import { transformAPIResponseToFormData } from '@utils/apiTransform';

const initialFormData: CustomFormData = {
  heightFeet: null,
  heightInch: null,
  weight: null,
  weightUnit: "lbs",
  bmi: null,
  neckCircumference: null,
  waistCircumference: null,
  tonsilSize: null,
  ahi: null,
  odi: null,
  rei: null,
  plmi: null,
  sleepStage: null,
  meanHeartRate: null,
  oxygenSaturation: null,
  arousalIndex: null,
  respiratoryDisturbanceIndex: null,
  sleepPosition: null,
  sleepDuration: null,
  nocturia: null,
  snoring: null,
  witnessedApnea: null,
  breathingStops: null,
  morningHeadaches: null,
  daytimeFatigue: null,
  morningDryMouth: null,
  hypertension: null,
  diabetes: null,
  thyroidFunction: null,
  familyHistory: null,
  smokingStatus: null,
  alcoholConsumption: null,
  physicalActivity: null,
  riskScore: null,
  riskLevel: null,
  receiveInfo: false,
  riskFactors: [],
  recommendations: [],
};

export const useFormData = () => {
  const [formData, setFormData] = useState<CustomFormData>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const { isAuthenticated, userToken } = useAuth();

  useEffect(() => {
    const fetchLatestAssessment = async () => {
      if (!isAuthenticated || !userToken) {
        setIsInitialLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await apiService.getLatestAssessment();

        if (response.has_assessment && response.assessment) {
          const convertedData: CustomFormData = {
            ...initialFormData,
            ...transformAPIResponseToFormData(response.assessment),
          };
          setFormData(convertedData);
        }
      } catch {
      } finally {
        setIsLoading(false);
        setIsInitialLoading(false);
      }
    };

    fetchLatestAssessment();
  }, [isAuthenticated, userToken]);

  const updateFormData = useCallback((updates: Partial<CustomFormData>) => {
    setFormData((prev: CustomFormData) => ({ ...prev, ...updates }));
  }, []);

  const safeParseFloat = useCallback((value: string | null | undefined): number | null => {
    if (!value) return null;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }, []);

  const calculateAndSetRiskScore = useCallback(async (forceAuth?: {
    isAuthenticated: boolean;
    userToken: string | null;
  }) => {
    try {
      setIsLoading(true);
      setError(null);

      const auth = forceAuth || { isAuthenticated, userToken };
      // BaseService interceptor will automatically add token if authenticated
      const isGuest = !auth.isAuthenticated || !auth.userToken;
      const result = await apiService.submitAssessment(formData, isGuest);

      const updatedData: Partial<CustomFormData> = {
        riskScore: result.risk_score,
        riskLevel: result.risk_level,
        riskFactors: result.risk_factors || [],
        recommendations: result.recommendations || [],
        bmi: result.bmi,
      };

      updateFormData(updatedData);

      return {
        riskScore: result.risk_score,
        riskLevel: result.risk_level,
        riskFactors: result.risk_factors || [],
        recommendations: result.recommendations || [],
        bmi: result.bmi,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to calculate risk score';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [formData, updateFormData, isAuthenticated, userToken]);

  const resetFormData = useCallback(() => {
    setFormData(initialFormData);
    setError(null);
  }, []);

  return {
    formData,
    updateFormData,
    calculateAndSetRiskScore,
    resetFormData,
    isLoading,
    error,
    isInitialLoading,
    safeParseFloat,
  };
};
